import React, { memo, type ComponentType } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import type { PercentPosition } from '@atlassian/jira-software-deployments-common-table/src/components/table/common/utils/timeline';
import type { TimelineMode } from '@atlassian/jira-software-deployments-common-table/src/model/timeline';
import type { Deployment } from '../../../../common/types';
import {
	type GroupedDeployments,
	getMostMeaningfulDeployment,
	getColorForDeploymentState,
	areDeploymentsEqual,
	getGroupedDeployments,
} from '../../../../common/utils/deployments';
import { getLinePositionBetweenDeployments } from './utils';

type Props = {
	deployments: Deployment[];
	getPositionFromDate: (date: Date) => PercentPosition;
	timelineMode: TimelineMode;
};

const Lines = ({ deployments: deploymentsSorted, getPositionFromDate, timelineMode }: Props) => {
	const deploymentGroups = getGroupedDeployments(
		deploymentsSorted,
		getPositionFromDate,
		timelineMode,
	);

	return deploymentGroups.map((group: GroupedDeployments, groupIndex: number) => {
		const nextDeploymentsBatch: Deployment[] | undefined = deploymentGroups[groupIndex + 1]
			? deploymentGroups[groupIndex + 1].deployments
			: undefined;
		const deployment = getMostMeaningfulDeployment(group.deployments);
		const nextDeployment = getMostMeaningfulDeployment(nextDeploymentsBatch || []);

		if (nextDeploymentsBatch === undefined) {
			return null;
		}

		const linePosition: PercentPosition = getLinePositionBetweenDeployments(
			nextDeployment,
			deployment,
			getPositionFromDate,
		);

		const color = getColorForDeploymentState(deployment.state);

		return (
			<Line
				key={deployment.id}
				leftPosition={linePosition.left}
				rightPosition={linePosition.right}
				color={color}
			/>
		);
	});
};

const isEqual = (
	{ deployments: prevDeployments, timelineMode: prevTimelineMode }: Props,
	{ deployments: nextDeployments, timelineMode: nextTimelineMode }: Props,
): boolean =>
	prevTimelineMode === nextTimelineMode && areDeploymentsEqual(prevDeployments, nextDeployments);

type LineProps = {
	leftPosition: number;
	rightPosition: number;
	color: string;
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Line: ComponentType<LineProps> = styled.div({
	height: token('space.025', '2px'),
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ color }) => color,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ leftPosition, rightPosition }: LineProps) => ({
		left: `${leftPosition}%`,
		right: `${rightPosition}%`,
	}),
});

export default memo<Props>(Lines, isEqual);
